// client/src/components/pages/LexiStar/Replay/ReplayPanel.jsx
//
// Replay side panel (LS-09 §5.6; FR-RPL-005): stops and idle periods with
// duration and place, source / destination / weighbridge visits with dwell
// times, and deviations (approved vs unapproved). Every row seeks the replay
// to its start (and the map follows the clock).

import React, { useState } from 'react';
import { fmtDuration, fmtKm } from '../StatusBoard/format';
import { clockTime } from './replayLogic';

const durationS = (row) =>
  Number.isFinite(Number(row.duration_s))
    ? Number(row.duration_s)
    : Number.isFinite(Number(row.dwell_s))
      ? Number(row.dwell_s)
      : Number.isFinite(row.to) && Number.isFinite(row.from)
        ? (row.to - row.from) / 1000
        : null;

const placeOf = (row) =>
  row.geofence_name ||
  row.geofence?.name ||
  (typeof row.geofence === 'string' ? row.geofence : null) ||
  row.place ||
  row.name ||
  (row.lat != null && row.lon != null
    ? `${Number(row.lat).toFixed(4)}, ${Number(row.lon).toFixed(4)}`
    : 'Unknown place');

const MOTION_LABELS = { PARKED: 'parked', IDLING: 'idling', STOPPED: 'stopped' };

const PURPOSE_LABELS = {
  source: 'Source',
  destination: 'Destination',
  weighbridge: 'Weighbridge',
  tipping: 'Tipping zone',
  delay: 'Delay zone',
  border: 'Border post',
  depot: 'Depot',
  fuel: 'Fuel station',
};

function Item({ onClick, label, children }) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        aria-label={label}
        className="w-full text-left px-4 py-2.5 hover:bg-slate-50 focus-visible:outline-none focus-visible:bg-slate-50"
      >
        {children}
      </button>
    </li>
  );
}

export default function ReplayPanel({ replay, onSeek }) {
  const [tab, setTab] = useState('stops');
  const tabs = [
    { value: 'stops', label: 'Stops', badge: replay.stops.length },
    { value: 'idles', label: 'Idles', badge: replay.idles.length },
    { value: 'visits', label: 'Visits', badge: replay.visits.length },
    {
      value: 'deviations',
      label: 'Deviations',
      badge: replay.deviations.length,
    },
  ];

  const empty = (text) => <li className="px-4 py-6 text-sm text-slate-500">{text}</li>;

  let body;
  if (tab === 'stops' || tab === 'idles') {
    const rows = tab === 'stops' ? replay.stops : replay.idles;
    body = rows.length
      ? rows.map((s, i) => (
          <Item
            key={`${tab}-${s.from}-${i}`}
            onClick={() => onSeek(s.from, s)}
            label={`${tab === 'stops' ? 'Stop' : 'Idle'} at ${clockTime(s.from, false)}, ${fmtDuration(durationS(s))}, ${placeOf(s)}`}
          >
            <span className="flex items-baseline justify-between gap-2">
              <span className="text-sm font-medium text-slate-900 truncate">{placeOf(s)}</span>
              <span className="text-sm tabular-nums text-slate-900 whitespace-nowrap">
                {fmtDuration(durationS(s))}
              </span>
            </span>
            <span className="block text-xs text-slate-500 tabular-nums">
              {clockTime(s.from, false)}–{clockTime(s.to, false)}
              {MOTION_LABELS[s.motion] ? ` · ${MOTION_LABELS[s.motion]}` : ''}
              {tab === 'idles' && !s.geofence && (
                <span className="ml-2 text-amber-700">outside known sites</span>
              )}
            </span>
          </Item>
        ))
      : empty(tab === 'stops' ? 'No stops in this window.' : 'No idle periods in this window.');
  } else if (tab === 'visits') {
    body = replay.visits.length
      ? replay.visits.map((v, i) => {
          const purpose = String(
            v.purpose_type || v.purpose || v.geofence_purpose || v.role || ''
          ).toLowerCase();
          return (
            <Item
              key={`visit-${v.from}-${i}`}
              onClick={() => onSeek(v.from, v)}
              label={`Visit ${placeOf(v)} at ${clockTime(v.from, false)}, dwell ${fmtDuration(durationS(v))}`}
            >
              <span className="flex items-baseline justify-between gap-2">
                <span className="text-sm font-medium text-slate-900 truncate">{placeOf(v)}</span>
                <span className="text-sm tabular-nums text-slate-900 whitespace-nowrap">
                  {fmtDuration(durationS(v))}
                </span>
              </span>
              <span className="block text-xs text-slate-500 tabular-nums">
                {PURPOSE_LABELS[purpose] || (purpose ? purpose : 'Geofence')} ·{' '}
                {clockTime(v.from, false)}–{clockTime(v.to, false)}
              </span>
            </Item>
          );
        })
      : empty('No geofence visits in this window.');
  } else {
    body = replay.deviations.length
      ? replay.deviations.map((d, i) => (
          <Item
            key={`dev-${d.id || i}`}
            onClick={() => onSeek(d.from, d)}
            label={`${d.approved ? 'Approved detour' : 'Unapproved deviation'} at ${clockTime(d.from, false)}`}
          >
            <span className="flex items-baseline justify-between gap-2">
              <span className="text-sm font-medium text-slate-900">
                <span aria-hidden="true" className="text-pink-700">
                  ⤴{' '}
                </span>
                {d.approved ? 'Approved detour' : 'Unapproved deviation'}
              </span>
              <span className="text-sm tabular-nums text-slate-900">
                {fmtKm(d.off_route_km, 1)} off route
              </span>
            </span>
            <span className="block text-xs text-slate-500 tabular-nums">
              {clockTime(d.from, false)}–{Number.isFinite(d.to) ? clockTime(d.to, false) : 'open'}
              {d.state ? ` · ${String(d.state).replace(/_/g, ' ').toLowerCase()}` : ''}
            </span>
          </Item>
        ))
      : empty('No deviations in this window.');
  }

  return (
    <div className="flex flex-col min-h-0">
      <div className="p-3 border-b border-slate-200">
        {/* Tabs wrap into as many columns as fit (two at 1440 px, one in a
            narrow panel) so a label and its count never collide. */}
        <div
          role="tablist"
          aria-label="Replay lists"
          className="grid grid-cols-[repeat(auto-fit,minmax(7.5rem,1fr))] gap-1 p-1 rounded-lg bg-surface-sunken ring-1 ring-inset ring-slate-200"
        >
          {tabs.map((t) => (
            <button
              key={t.value}
              type="button"
              role="tab"
              aria-selected={tab === t.value}
              onClick={() => setTab(t.value)}
              className={`inline-flex items-center justify-between gap-2 px-2.5 h-8 rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
                tab === t.value
                  ? 'bg-surface-raised text-slate-900 shadow-sm ring-1 ring-slate-200'
                  : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              <span className="whitespace-nowrap">{t.label}</span>
              <span className="flex-shrink-0 rounded-full px-1.5 text-[11px] leading-4 tabular-nums bg-slate-200 text-slate-600">
                {t.badge}
              </span>
            </button>
          ))}
        </div>
      </div>
      <ul
        className="divide-y divide-slate-100 overflow-y-auto"
        aria-label={tabs.find((t) => t.value === tab).label}
      >
        {body}
      </ul>
    </div>
  );
}
